import { replyMessageSchema, type ReplyMessage } from "@go-novus/admin-api";
import { io } from "socket.io-client";
import { z } from "zod";

const CONNECT_TIMEOUT_MS = 8000;
const UI_EVENT_ERROR = {
  invalid: "invalid_request",
  timeout: "ack_timeout",
  invalidAck: "invalid_ack",
} as const;

/** 对应 `contract/schemas/send-message.schema.json`。`context` 是只增不减的扩展点，不做 strict。 */
const sendMessageFrameSchema = z.object({
  conversation_id: z.string().optional(),
  tenant_id: z.number().int(),
  uid: z.string(),
  content: z.string(),
  role: z.literal("user"),
  msg_id: z.string(),
  source: z.literal("user"),
  context: z.looseObject({}),
});

/** 对应 `contract/schemas/send-message-ack.schema.json`：同步 ack 只表示收下。 */
const sendMessageAckSchema = z.object({
  ok: z.boolean(),
  message_id: z.string(),
  conversation_id: z.string(),
  /** 收下时后端不发这个键。收紧成 `.nullable()` 会把每条消息都判成被拒。 */
  reason: z.string().nullish(),
});

export type SendMessageFrame = z.infer<typeof sendMessageFrameSchema>;
export type SendMessageAck = z.infer<typeof sendMessageAckSchema>;

const eventScope = {
  tenant_id: z.string().min(1),
  uid: z.string().min(1),
  conversation_id: z.string().min(1),
  event_id: z.string().min(1).max(128),
};
const taskVersion = z.int().min(1).max(2147483647);
export const uiEventFrameSchema = z.union([
  z.strictObject({
    ...eventScope,
    event: z.literal("order_selected"),
    task_version: taskVersion,
    payload: z.strictObject({ order_ref: z.string().min(1).max(512) }),
  }),
  z.strictObject({
    ...eventScope,
    event: z.literal("order_card_action"),
    task_version: taskVersion,
    payload: z.strictObject({
      order_ref: z.string().min(1).max(512),
      action: z.literal("not_received"),
    }),
  }),
  z.strictObject({
    ...eventScope,
    event: z.literal("start_order_check"),
    payload: z.strictObject({}),
  }),
]);
export type UiEventFrame = z.infer<typeof uiEventFrameSchema>;
type WithoutScope<T> = T extends unknown ? Omit<T, "tenant_id" | "uid"> : never;
export type UiEventInput = WithoutScope<UiEventFrame>;
export type UiEventAck = {
  event_id: string;
  accepted: boolean;
  error?: string;
};

/** 协议层只用到 socket.io 客户端的这几个方法，测试注入假实现时按这个形状实现即可。 */
export interface SocketLike {
  emit(
    event: string,
    frame: SendMessageFrame | UiEventFrame,
    ack: (response: unknown) => void,
  ): unknown;
  on(event: string, handler: (frame: unknown) => void): unknown;
  off(event: string, handler: (frame: unknown) => void): unknown;
  disconnect(): unknown;
}

export interface IoOptions {
  query: { token: string; authType: string; connectionType: string };
  transports: string[];
  reconnection: boolean;
  timeout: number;
}

export type IoFactory = (url: string, options: IoOptions) => SocketLike;

export interface ConnectSocketInput {
  socketUrl: string;
  token: string;
  tenantId: number;
  uid: string;
  ioFactory?: IoFactory;
  onInvalidated?: () => void;
}

export interface SendMessageInput {
  content: string;
  /** 同一条消息失败重试时保持不变，供后端去重。 */
  msgId?: string;
  /** 第一条消息可以不带，后端会按 (tenant_id, uid) 落到同一条 Conversation。 */
  conversationId?: string;
  context?: Record<string, unknown>;
}

export type SendMessageResult =
  | { status: "acked"; msgId: string; ack: SendMessageAck }
  | { status: "rejected"; msgId: string; detail: string };

export interface WidgetSocket {
  sendUiEvent?(input: UiEventInput): Promise<UiEventAck>;
  sendMessage(input: SendMessageInput): Promise<SendMessageResult>;
  /** 返回退订函数。解析不了的帧直接丢掉。 */
  onReply(handler: (frame: ReplyMessage) => void): () => void;
  /**
   * 底层连接(重)建立时触发(GON-127):断线重连后令牌里的顾客声明
   * 可能已死,只作复查信号。返回退订函数。
   */
  onReconnect(handler: () => void): () => void;
  disconnect(): void;
}

export function connectSocket({
  socketUrl,
  token,
  tenantId,
  uid,
  ioFactory = defaultIoFactory,
  onInvalidated,
}: ConnectSocketInput): WidgetSocket {
  const socket = ioFactory(socketUrl, {
    query: { token, authType: "shopify", connectionType: "shopify" },
    transports: ["websocket", "polling"],
    reconnection: false,
    timeout: CONNECT_TIMEOUT_MS,
  });

  let invalidated = false;
  const pending = new Set<() => void>();
  const invalidate = () => {
    if (invalidated) return;
    invalidated = true;
    for (const reject of pending) reject();
    pending.clear();
    socket.disconnect();
    onInvalidated?.();
  };
  socket.on("disconnect", invalidate);
  socket.on("connect_error", invalidate);
  return {
    sendUiEvent(input) {
      if (invalidated)
        return Promise.resolve({
          event_id: input.event_id,
          accepted: false,
          error: UI_EVENT_ERROR.timeout,
        });
      const frame = uiEventFrameSchema.safeParse({
        ...input,
        tenant_id: String(tenantId),
        uid,
      });
      if (!frame.success)
        return Promise.resolve({
          event_id: input.event_id,
          accepted: false,
          error: UI_EVENT_ERROR.invalid,
        });
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          pending.delete(reject);
          resolve({
            event_id: input.event_id,
            accepted: false,
            error: UI_EVENT_ERROR.timeout,
          });
        }, CONNECT_TIMEOUT_MS);
        const reject = () => {
          clearTimeout(timer);
          resolve({
            event_id: input.event_id,
            accepted: false,
            error: UI_EVENT_ERROR.timeout,
          });
        };
        pending.add(reject);
        socket.emit("ui-event", frame.data, (response) => {
          pending.delete(reject);
          clearTimeout(timer);
          const ack = z
            .object({
              event_id: z.string(),
              accepted: z.boolean(),
              error: z.enum(["invalid_request", "rate_limited"]).optional(),
            })
            .safeParse(response);
          resolve(
            ack.success && ack.data.event_id === input.event_id
              ? ack.data
              : {
                  event_id: input.event_id,
                  accepted: false,
                  error: UI_EVENT_ERROR.invalidAck,
                },
          );
        });
      });
    },
    sendMessage(input) {
      const msgId = input.msgId ?? createMessageId();
      if (invalidated)
        return Promise.resolve({
          status: "rejected",
          msgId,
          detail: "socket_disconnected",
        });
      const frame: SendMessageFrame = {
        ...(input.conversationId === undefined
          ? {}
          : { conversation_id: input.conversationId }),
        tenant_id: tenantId,
        uid,
        content: input.content,
        role: "user",
        msg_id: msgId,
        source: "user",
        context: input.context ?? {},
      };
      // 宿主可能是没有类型检查的 JS（liquid block），帧发出去之前按契约兜一道。
      const validated = sendMessageFrameSchema.safeParse(frame);
      if (!validated.success) {
        return Promise.resolve({
          status: "rejected" as const,
          msgId,
          detail: z.prettifyError(validated.error),
        });
      }

      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          pending.delete(reject);
          resolve({
            status: "rejected",
            msgId,
            // 这条只进日志与 devtools：买家看到的是 conversation.ts 里的本地文案。
            // eslint-disable-next-line no-restricted-syntax
            detail: "Message acknowledgement timed out",
          });
        }, CONNECT_TIMEOUT_MS);
        const reject = () => {
          clearTimeout(timer);
          resolve({ status: "rejected", msgId, detail: "socket_disconnected" });
        };
        pending.add(reject);
        socket.emit("send-message", validated.data, (response: unknown) => {
          pending.delete(reject);
          clearTimeout(timer);
          const parsed = sendMessageAckSchema.safeParse(response);
          resolve(
            parsed.success
              ? { status: "acked", msgId, ack: parsed.data }
              : {
                  status: "rejected",
                  msgId,
                  detail: z.prettifyError(parsed.error),
                },
          );
        });
      });
    },

    onReply(handler) {
      const listener = (frame: unknown): void => {
        const envelope = z
          .looseObject({
            transfer_reason: z
              .unknown()
              .transform(() => undefined)
              .optional(),
          })
          .safeParse(frame);
        if (!envelope.success) return;
        const parsed = replyMessageSchema.safeParse(envelope.data);
        if (!invalidated && parsed.success) handler(parsed.data);
      };
      socket.on("reply-message", listener);
      return () => {
        socket.off("reply-message", listener);
      };
    },

    onReconnect(handler) {
      // socket.io 的 connect 在首次连接与每次重连都会触发;首次那次
      // 也被复查节流吸收,不会多打一次请求。
      const listener = (): void => {
        if (!invalidated) handler();
      };
      socket.on("connect", listener);
      return () => {
        socket.off("connect", listener);
      };
    },

    disconnect: invalidate,
  };
}

const defaultIoFactory: IoFactory = (url, options) => io(url, options);

function createMessageId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `msg_${String(Date.now())}_${Math.random().toString(36).slice(2)}`;
}
